import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { dataGridPremiumColumns } from '../../utils/helper';

import { DataGridIdentifiers, AddButtonUi, DatagridPremiumUi, FormErrorUI, AutoCompleteSelectUI } from '../Interface';
import dayjs from 'dayjs';

function EditToolbar(props) {
  const { disabled, push, defAssignment } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newData = {
      ...defAssignment,
      id: uuidv4(),
    };

    await push(newData);

    const fieldsToValidate = [`assignments.${values.assignments.length ?? 0}.userId`];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
}

const LicenseAssignment = ({ disabled, defAssignment, autoCompleteOptions, autoCompleteData }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const getValueIndex = (values, id) => {
    return values.assignments.findIndex((rc) => rc.id === id);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'userId',
        headerName: 'Felhasználókód',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          const findedItem = autoCompleteOptions.user.find((u) => {
            const userId = values.assignments?.[valueIndex]?.userId;
            return u.value === userId;
          });

          return (
            <Field
              name={`assignments.${valueIndex}.userId`}
              as={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors?.assignments?.[valueIndex]?.userId} />}
              selectedValue={values.assignments?.[valueIndex]?.userId}
              selectedLabelValue={findedItem?.label}
              onChange={(_e, newVal) => {
                setFieldValue(`assignments.${valueIndex}.userId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
              }}
              dataset={autoCompleteOptions.user}
              onBlur={async () => await validateField(`assignments.${valueIndex}.userId`)}
            />
          );
        },
      },
      {
        field: 'nickName',
        headerName: 'Felhasználónév',
        type: 'string',
        editable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const foundUser = autoCompleteData?.user?.find((u) => u.userId === values.assignments?.[valueIndex]?.userId);

          return foundUser?.nickName ?? '';
        },
      },
      {
        field: 'createdAt',
        headerName: 'Hozzárendelés időpontja',
        type: 'date',
        editable: false,
        type: 'dateTime',
        valueGetter: (_data, row) => {
          return row.createdAt ? new Date(row.createdAt) : null;
        },
        renderCell: (data) => {
          return data.row.createdAt ? dayjs(data.row.createdAt).format('YYYY.MM.DD. HH:mm') : '';
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="assignments">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.LicenseAssignmentTable}
                height={400}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.assignments ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                headerFilters={false}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defAssignment,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                editMode="row"
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default LicenseAssignment;
